import React, { useState } from 'react';
import countries from 'i18n-iso-countries';
import en from 'i18n-iso-countries/langs/en.json';

countries.registerLocale(en);

const countryList = Object.entries(
  countries.getNames('en', { select: 'official' })
)
  .map(([code, name]) => ({ code, name }))
  .sort((a, b) => a.name.localeCompare(b.name));

// Pin UAE at the top for regional UX
const COUNTRIES = [
  { code: 'AE', name: 'United Arab Emirates' },
  ...countryList.filter(c => c.code !== 'AE'),
];
import { useNavigate, Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Check,
  ArrowRight,
  Building,
  User,
  Zap,
  CheckCircle2,
  Target,
  Cpu,
  ChevronLeft
} from 'lucide-react';
import { Button, Input, Card, Badge } from '../components/UI';
import { OtpInput } from '../components/OtpInput';
import { useStore } from '../store/useStore';
import { calculateRisk, cn } from '../lib/utils';
import { toast } from 'sonner';
import LensVerseLogo from './LenseverseLogo.png';

const API_BASE = import.meta.env.VITE_API_URL || '';


const FUNCTIONS = [
  'Business Development',
  'Engineering',
  'Finance',
  'HR',
  'IT',
  'Legal',
  'Marketing',
  'Operations',
  'Product',
  'Sales',
  'Other',
];

// ─────────────────────────────────────────────────────────────────────────────
// Shared OTP helpers (used by both flows)
// ─────────────────────────────────────────────────────────────────────────────

type AccountType = null | 'individual' | 'organisation';

const Signup: React.FC = () => {
  const navigate = useNavigate();
  const { requests, addRequest } = useStore();

  // ── Top-level account type selection ──────────────────────────────────────
  const [accountType, setAccountType] = useState<AccountType>(null);

  // ── Shared OTP state ──────────────────────────────────────────────────────
  const [isLoading, setIsLoading] = useState(false);
  const [isOtpVerified, setIsOtpVerified] = useState(false);
  const [showOtpInput, setShowOtpInput] = useState(false);

  // ── Organisation (4-step) wizard state ───────────────────────────────────
  const [step, setStep] = useState(1);
  const [agreedToTerms, setAgreedToTerms] = useState(false);
  const [orgForm, setOrgForm] = useState({
    email: '',
    name: '',
    companyName: '',
    companyDomain: '',
    country: 'United Arab Emirates',
    companySize: '51-200',
    useCases: [] as string[],
    volumeBucket: '10,000 - 50,000',
    deploymentPreference: 'saas' as 'saas' | 'onprem',
  });

  // ── Individual form state ─────────────────────────────────────────────────
  const [indForm, setIndForm] = useState({
    firstName: '',
    lastName: '',
    email: '',
    country: 'United Arab Emirates',
    jobTitle: '',
    function: 'Business Development',
    companyName: '',
  });

  // ─────────────────────────────────────────────────────────────────────────
  // OTP handlers — used by BOTH flows. `email` is pulled from the active form.
  // ─────────────────────────────────────────────────────────────────────────

  const activeEmail = accountType === 'individual' ? indForm.email : orgForm.email;

  // Signup-specific OTP session (separate from login OTP in useStore)
  const [signupSessionId, setSignupSessionId] = useState<string | null>(null);

  const handleEmailVerification = async () => {
    if (!activeEmail) return;
    setIsLoading(true);
    try {
      const res = await fetch(`${API_BASE}/api/auth/signup/send-otp/`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: activeEmail }),
      });
      const data = await res.json();
      if (res.ok && data.session_id) {
        setSignupSessionId(data.session_id);
        setShowOtpInput(true);
        toast.success('Access token dispatched', { description: 'Check your secure inbox.' });
      } else {
        toast.error('Failed to send OTP.');
      }
    } catch (err) {
      console.error('Signup OTP send error:', err);
      toast.error('Failed to send OTP.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleOtpComplete = async (code: string) => {
    if (!signupSessionId) {
      toast.error('No OTP session. Please request a new code.');
      return;
    }
    setIsLoading(true);
    try {
      const res = await fetch(`${API_BASE}/api/auth/signup/verify-otp/`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ session_id: signupSessionId, code }),
      });
      const data = await res.json();
      if (res.ok && data.success) {
        setIsOtpVerified(true);
        setShowOtpInput(false);
        toast.success('Identity Verified', { description: 'Secure email verification complete.' });
      } else {
        toast.error(data.detail || 'Invalid or expired verification code.');
      }
    } catch (err) {
      console.error('OTP verify error:', err);
      toast.error('Verification failed.');
    } finally {
      setIsLoading(false);
    }
  };

  // ─────────────────────────────────────────────────────────────────────────
  // Organisation submit
  // ─────────────────────────────────────────────────────────────────────────

  const handleOrgSubmit = async () => {
    setIsLoading(true);
    try {
      const res = await fetch(`${API_BASE}/api/auth/signup/submit/`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: orgForm.name,
          email: orgForm.email,
          emailVerified: isOtpVerified,
          companyName: orgForm.companyName,
          companyDomain: orgForm.companyDomain,
          country: orgForm.country,
          companySize: orgForm.companySize,
          useCases: orgForm.useCases,
          volumeBucket: orgForm.volumeBucket,
          deploymentPreference: orgForm.deploymentPreference,
          ...(() => {
            const { score, signals, level } = calculateRisk(orgForm.email, orgForm.companyDomain, requests);
            return { riskScore: score, riskSignals: signals, riskLevel: level };
          })(),
        }),
      });
      const data = await res.json();
      if (res.ok && data.success) {
        navigate('/auth/signup/success', { state: { requestId: data.requestId } });
      } else {
        toast.error(data.error || 'Submission failed. Please try again.');
      }
    } catch (err) {
      console.error('Signup submit error:', err);
      toast.error('Network error. Please check your connection.');
    } finally {
      setIsLoading(false);
    }
  };

  // ─────────────────────────────────────────────────────────────────────────
  // Individual submit
  // ─────────────────────────────────────────────────────────────────────────

  const handleIndividualSubmit = async () => {
    setIsLoading(true);
    try {
      const res = await fetch(`${API_BASE}/api/auth/signup/individual/submit/`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          firstName: indForm.firstName,
          lastName: indForm.lastName,
          email: indForm.email,
          emailVerified: isOtpVerified,
          country: indForm.country,
          jobTitle: indForm.jobTitle,
          function: indForm.function,
          companyName: indForm.companyName,
        }),
      });
      const data = await res.json();
      if (res.ok && data.success) {
        navigate('/auth/signup/success', { state: { requestId: data.requestId } });
      } else {
        toast.error(data.error || 'Submission failed. Please try again.');
      }
    } catch (err) {
      console.error('Individual signup error:', err);
      toast.error('Network error. Please check your connection.');
    } finally {
      setIsLoading(false);
    }
  };

  // ─────────────────────────────────────────────────────────────────────────
  // Org wizard helpers
  // ─────────────────────────────────────────────────────────────────────────

  const nextStep = () => setStep(s => s + 1);
  const prevStep = () => setStep(s => s - 1);
  const stepLabels = ['Account Setup', 'Company Details', 'Workspace Settings', 'Review & Finish'];

  // ─────────────────────────────────────────────────────────────────────────
  // Render
  // ─────────────────────────────────────────────────────────────────────────

  return (
    <div className="min-h-screen bg-deep-0 flex flex-col relative overflow-hidden">
      <div className="radar-sweep opacity-10" />

      <div className="flex-1 flex flex-col w-full px-6 pt-4 pb-4 md:px-10 md:pt-6">

        {/* Header HUD */}
        <div className="max-w-6xl mx-auto w-full flex items-center justify-between mb-6 relative z-10">
          <Link to="/" className="flex items-center gap-3">
            <span className="text-xl font-bold text-white tracking-tight">Sign Up</span>
          </Link>
          <div className="flex items-center gap-6">
            {accountType === 'organisation' && (
              <div className="hidden md:flex flex-col items-end">
                <span className="text-xs font-semibold text-white/50">Profile Completeness</span>
                <div className="w-40 h-1 bg-white/5 rounded-full mt-1 overflow-hidden">
                  <motion.div animate={{ width: `${(step / 4) * 100}%` }} className="h-full bg-accent" />
                </div>
              </div>
            )}
            <Button
              variant="outline"
              size="sm"
              className="border-white/20 text-white hover:bg-white/10 hover:border-white/40 px-5 transition-all"
              onClick={() => navigate('/auth/login')}
            >
              Login
            </Button>
          </div>
        </div>

        {/* ═══════════════════════════════════════════════════════════════════ */}
        {/* STEP 0 — Account Type Picker                                       */}
        {/* ═══════════════════════════════════════════════════════════════════ */}
        {accountType === null && (
          <div className="max-w-3xl mx-auto w-full flex-1 relative z-10 flex flex-col items-center justify-center">
            <AnimatePresence mode="wait">
              <motion.div
                key="picker"
                initial={{ opacity: 0, y: 24 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -24 }}
                className="w-full space-y-10"
              >
                <div className="space-y-3 pl-2">
                  <h2 className="text-5xl font-black text-white tracking-tighter">Request Access</h2>
                  <p className="text-white/40 font-medium">How would you like to access LensVerse?</p>
                </div>

                <div className="grid md:grid-cols-2 gap-6">
                  {/* Individual card */}
                  <motion.button
                    whileHover={{ scale: 1.02, y: -4 }}
                    whileTap={{ scale: 0.98 }}
                    onClick={() => setAccountType('individual')}
                    className="group relative p-8 rounded-3xl bg-deep-surface border border-white/10 hover:border-accent/40 hover:bg-accent/[0.04] transition-all text-left space-y-5 overflow-hidden"
                  >
                    <div className="absolute inset-0 bg-gradient-to-br from-accent/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
                    <div className="w-14 h-14 rounded-2xl bg-white/5 border border-white/10 group-hover:border-accent/30 group-hover:bg-accent/10 flex items-center justify-center transition-all">
                      <User className="w-7 h-7 text-white/40 group-hover:text-accent transition-colors" />
                    </div>
                    <div className="space-y-2 relative z-10">
                      <h3 className="text-xl font-bold text-white">Individual</h3>
                      <p className="text-white/50 text-sm font-medium leading-relaxed">
                        Personal access for freelancers, consultants, and individual professionals.
                      </p>
                    </div>
                    <div className="flex items-center gap-2 text-xs font-semibold text-white/30 group-hover:text-accent/70 transition-colors relative z-10">
                      Get started <ArrowRight className="w-3.5 h-3.5" />
                    </div>
                  </motion.button>

                  {/* Organisation card */}
                  <motion.button
                    whileHover={{ scale: 1.02, y: -4 }}
                    whileTap={{ scale: 0.98 }}
                    onClick={() => setAccountType('organisation')}
                    className="group relative p-8 rounded-3xl bg-deep-surface border border-white/10 hover:border-accent/40 hover:bg-accent/[0.04] transition-all text-left space-y-5 overflow-hidden"
                  >
                    <div className="absolute inset-0 bg-gradient-to-br from-accent/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
                    <div className="w-14 h-14 rounded-2xl bg-white/5 border border-white/10 group-hover:border-accent/30 group-hover:bg-accent/10 flex items-center justify-center transition-all">
                      <Building className="w-7 h-7 text-white/40 group-hover:text-accent transition-colors" />
                    </div>
                    <div className="space-y-2 relative z-10">
                      <h3 className="text-xl font-bold text-white">Organisation</h3>
                      <p className="text-white/50 text-sm font-medium leading-relaxed">
                        Team and enterprise access for companies, agencies, and government entities.
                      </p>
                    </div>
                    <div className="flex items-center gap-2 text-xs font-semibold text-white/30 group-hover:text-accent/70 transition-colors relative z-10">
                      Get started <ArrowRight className="w-3.5 h-3.5" />
                    </div>
                  </motion.button>
                </div>
              </motion.div>
            </AnimatePresence>
          </div>
        )}

        {/* ═══════════════════════════════════════════════════════════════════ */}
        {/* INDIVIDUAL FLOW                                                     */}
        {/* ═══════════════════════════════════════════════════════════════════ */}
        {accountType === 'individual' && (
          <div className="max-w-2xl mx-auto w-full flex-1 relative z-10">
            <AnimatePresence mode="wait">
              <motion.div
                key="individual-form"
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                className="space-y-5"
              >
                {/* Back + heading */}
                <div className="space-y-2">
                  <button
                    onClick={() => { setAccountType(null); setIsOtpVerified(false); setShowOtpInput(false); }}
                    className="flex items-center gap-2 text-sm text-white/40 hover:text-white/70 transition-colors"
                  >
                    <ChevronLeft className="w-4 h-4" /> Back
                  </button>
                  <div className="space-y-1">
                    <div className="w-9 h-9 rounded-xl bg-accent/10 border border-accent/20 flex items-center justify-center">
                      <User className="w-4 h-4 text-accent" />
                    </div>
                    <h2 className="text-2xl font-bold text-white tracking-tight">Individual Access</h2>
                    <p className="text-white/50 text-sm font-medium">Tell us about yourself to request access.</p>
                  </div>
                </div>

                <div className="space-y-3">
                  {/* Name row */}
                  <div className="grid grid-cols-2 gap-4">
                    <Input
                      label="First Name"
                      placeholder="Sumit"
                      value={indForm.firstName}
                      onChange={e => setIndForm({ ...indForm, firstName: e.target.value })}
                    />
                    <Input
                      label="Last Name"
                      placeholder="Das"
                      value={indForm.lastName}
                      onChange={e => setIndForm({ ...indForm, lastName: e.target.value })}
                    />
                  </div>

                  {/* Country */}
                  <div className="space-y-2">
                    <label className="text-xs font-semibold text-white/50 pl-1">Country / Region</label>
                    <select
                      className="w-full h-11 px-4 bg-deep-surface border border-white/10 rounded-xl text-sm font-bold text-white focus:ring-2 focus:ring-accent outline-none transition-all"
                      value={indForm.country}
                      onChange={e => setIndForm({ ...indForm, country: e.target.value })}
                    >
                      {COUNTRIES.map(c => <option key={c.code} value={c.name}>{c.name}</option>)}
                    </select>
                  </div>

                  {/* Job title */}
                  <Input
                    label="Job Title"
                    placeholder="Founder"
                    value={indForm.jobTitle}
                    onChange={e => setIndForm({ ...indForm, jobTitle: e.target.value })}
                  />

                  {/* Function */}
                  <div className="space-y-2">
                    <label className="text-xs font-semibold text-white/50 pl-1">Function</label>
                    <select
                      className="w-full h-11 px-4 bg-deep-surface border border-white/10 rounded-xl text-sm font-bold text-white focus:ring-2 focus:ring-accent outline-none transition-all"
                      value={indForm.function}
                      onChange={e => setIndForm({ ...indForm, function: e.target.value })}
                    >
                      {FUNCTIONS.map(f => <option key={f}>{f}</option>)}
                    </select>
                  </div>

                  {/* Company name */}
                  <Input
                    label="Company Name"
                    placeholder="Vikashan Technologies"
                    value={indForm.companyName}
                    onChange={e => setIndForm({ ...indForm, companyName: e.target.value })}
                  />

                  {/* Email + OTP */}
                  <div className="space-y-4">
                    <div className="flex flex-col sm:flex-row items-stretch sm:items-end gap-3">
                      <div className="flex-1">
                        <Input
                          label="Email Address"
                          placeholder="you@example.com"
                          type="email"
                          disabled={isOtpVerified}
                          value={indForm.email}
                          onChange={e => setIndForm({ ...indForm, email: e.target.value })}
                        />
                      </div>
                      {!isOtpVerified ? (
                        <Button
                          variant="glass"
                          size="lg"
                          className="h-11 w-full sm:w-auto font-semibold text-sm whitespace-nowrap"
                          isLoading={isLoading}
                          disabled={!indForm.email.includes('@')}
                          onClick={handleEmailVerification}
                        >
                          Verify Email
                        </Button>
                      ) : (
                        <div className="h-11 w-11 bg-green-500/10 border border-green-500/20 rounded-xl flex items-center justify-center text-green-500 shadow-[0_0_15px_rgba(34,197,94,0.3)] shrink-0">
                          <CheckCircle2 className="w-5 h-5" />
                        </div>
                      )}
                    </div>

                    <AnimatePresence>
                      {showOtpInput && (
                        <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: 'auto', opacity: 1 }} className="p-6 bg-accent/5 border border-accent/20 rounded-2xl space-y-4">
                          <div className="flex items-center justify-between">
                            <span className="text-sm font-semibold text-accent">Enter Verification Code</span>
                            <span className="text-xs font-mono text-white/50">6 DIGITS REQ</span>
                          </div>
                          <OtpInput onComplete={handleOtpComplete} />
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>
                </div>

                <Button
                  className="w-full"
                  size="xl"
                  isLoading={isLoading}
                  disabled={
                    !isOtpVerified ||
                    !indForm.firstName ||
                    !indForm.lastName ||
                    !indForm.jobTitle ||
                    !indForm.companyName
                  }
                  onClick={handleIndividualSubmit}
                >
                  Submit Request <Zap className="ml-3 w-5 h-5" />
                </Button>
              </motion.div>
            </AnimatePresence>
          </div>
        )}

        {/* ═══════════════════════════════════════════════════════════════════ */}
        {/* ORGANISATION FLOW — existing 4-step wizard (unchanged logic)        */}
        {/* ═══════════════════════════════════════════════════════════════════ */}
        {accountType === 'organisation' && (
          <div className="max-w-6xl mx-auto w-full flex-1 relative z-10 flex flex-col md:flex-row gap-10 md:gap-16">

            {/* Vertical Stepper */}
            <div className="md:w-56 flex-shrink-0 relative">
              <div className="sticky top-20">
                <div className="relative flex flex-col gap-8 md:gap-12 pl-4">
                  {/* Back button above stepper */}
                  <button
                    onClick={() => { setAccountType(null); setStep(1); setIsOtpVerified(false); setShowOtpInput(false); }}
                    className="flex items-center gap-2 text-sm text-white/40 hover:text-white/70 transition-colors mb-2"
                  >
                    <ChevronLeft className="w-4 h-4" /> Back
                  </button>

                  <div className="absolute top-10 bottom-0 left-[1.75rem] w-px bg-white/5 -z-0" />
                  <motion.div
                    className="absolute top-10 left-[1.75rem] w-px bg-accent -z-0 origin-top"
                    initial={{ height: '0%' }}
                    animate={{ height: `${((step - 1) / 3) * 100}%` }}
                    transition={{ duration: 0.8, ease: 'easeInOut' }}
                  />
                  {stepLabels.map((label, i) => (
                    <div key={i} className="z-10 flex items-center gap-5 relative">
                      <div className={cn(
                        'w-12 h-12 rounded-2xl flex items-center justify-center text-sm font-black transition-all duration-700 relative shrink-0',
                        step === i + 1
                          ? 'bg-accent text-deep-0 shadow-[0_0_30px_rgba(247,108,28,0.5)] scale-110'
                          : step > i + 1
                            ? 'bg-green-500 text-deep-0 scale-90'
                            : 'bg-deep-surface border border-white/10 text-white/30 scale-90'
                      )}>
                        {step > i + 1 ? <Check className="w-5 h-5" strokeWidth={4} /> : `0${i + 1}`}
                        {step === i + 1 && (
                          <motion.div layoutId="step-glow" className="absolute inset-0 rounded-2xl ring-4 ring-accent/20 animate-pulse" />
                        )}
                      </div>
                      <span className={cn(
                        'text-sm font-semibold transition-all',
                        step >= i + 1 ? 'text-white' : 'text-white/40'
                      )}>{label}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Wizard Form */}
            <div className="flex-1 max-w-2xl pt-2">
              <AnimatePresence mode="wait">

                {/* ── STEP 1 ── */}
                {step === 1 && (
                  <motion.div key="s1" initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }} className="space-y-8">
                    <div className="space-y-2">
                      <div className="w-11 h-11 rounded-xl bg-accent/10 border border-accent/20 flex items-center justify-center">
                        <Target className="w-5 h-5 text-accent" />
                      </div>
                      <p className="text-white/50 text-base font-medium">Personal credentials for your workspace.</p>
                    </div>

                    <div className="space-y-6">
                      <Input
                        label="Full Name"
                        placeholder="Jane Doe"
                        value={orgForm.name}
                        onChange={e => setOrgForm({ ...orgForm, name: e.target.value.toUpperCase() })}
                        className="font-medium"
                      />

                      <div className="space-y-4">
                        <div className="flex flex-col sm:flex-row items-stretch sm:items-end gap-3">
                          <div className="flex-1">
                            <Input
                              label="Work Email"
                              placeholder="agent@enterprise.ae"
                              type="email"
                              disabled={isOtpVerified}
                              value={orgForm.email}
                              onChange={e => {
                                const email = e.target.value;
                                const domain = email.includes('@') ? email.split('@')[1] : '';
                                setOrgForm({ ...orgForm, email, companyDomain: domain });
                              }}
                            />
                          </div>
                          {!isOtpVerified ? (
                            <Button
                              variant="glass"
                              size="lg"
                              className="h-11 w-full sm:w-auto font-semibold text-sm whitespace-nowrap"
                              isLoading={isLoading}
                              disabled={!orgForm.email.includes('@')}
                              onClick={handleEmailVerification}
                            >
                              Verify Email
                            </Button>
                          ) : (
                            <div className="h-11 w-11 bg-green-500/10 border border-green-500/20 rounded-xl flex items-center justify-center text-green-500 shadow-[0_0_15px_rgba(34,197,94,0.3)] shrink-0">
                              <CheckCircle2 className="w-5 h-5" />
                            </div>
                          )}
                        </div>

                        <AnimatePresence>
                          {showOtpInput && (
                            <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: 'auto', opacity: 1 }} className="p-6 bg-accent/5 border border-accent/20 rounded-2xl space-y-4">
                              <div className="flex items-center justify-between">
                                <span className="text-sm font-semibold text-accent">Enter Verification Code</span>
                                <span className="text-xs font-mono text-white/50">6 DIGITS REQ</span>
                              </div>
                              <OtpInput onComplete={handleOtpComplete} />
                            </motion.div>
                          )}
                        </AnimatePresence>
                      </div>
                    </div>

                    <Button className="w-full" size="xl" disabled={!isOtpVerified || !orgForm.name} onClick={nextStep}>
                      Next Step <ArrowRight className="ml-3 w-5 h-5" />
                    </Button>
                  </motion.div>
                )}

                {/* ── STEP 2 ── */}
                {step === 2 && (
                  <motion.div key="s2" initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }} className="space-y-8">
                    <div className="space-y-2">
                      <div className="w-11 h-11 rounded-xl bg-accent/10 border border-accent/20 flex items-center justify-center">
                        <Building className="w-5 h-5 text-accent" />
                      </div>
                      <p className="text-white/50 text-base font-medium">Legal entity and domain information.</p>
                    </div>

                    <div className="grid grid-cols-2 gap-6">
                      <div className="col-span-2">
                        <Input
                          label="Company Name"
                          placeholder="Emirates Global Industries"
                          value={orgForm.companyName}
                          onChange={e => setOrgForm({ ...orgForm, companyName: e.target.value.toUpperCase() })}
                          className="font-medium"
                        />
                      </div>
                      <Input
                        label="Company Domain"
                        placeholder="eg-industries.ae"
                        value={orgForm.companyDomain}
                        onChange={e => setOrgForm({ ...orgForm, companyDomain: e.target.value })}
                      />
                      <div className="space-y-2">
                        <label className="text-xs font-semibold text-white/50 pl-1">Primary Region</label>
                        <select
                          className="w-full h-11 px-4 bg-deep-surface border border-white/10 rounded-xl text-sm font-bold text-white focus:ring-2 focus:ring-accent outline-none transition-all"
                          value={orgForm.country}
                          onChange={e => setOrgForm({ ...orgForm, country: e.target.value })}
                        >
                          {COUNTRIES.map(c => <option key={c.code} value={c.name}>{c.name}</option>)}
                        </select>
                      </div>
                      <div className="col-span-2 space-y-3">
                        <label className="text-xs font-semibold text-white/50 pl-1">Company Size</label>
                        <div className="grid grid-cols-4 gap-3">
                          {['1-50', '51-200', '201-1000', '1000+'].map(size => (
                            <button
                              key={size}
                              onClick={() => setOrgForm({ ...orgForm, companySize: size })}
                              className={cn(
                                'px-3 py-3 rounded-xl text-sm font-medium border transition-all',
                                orgForm.companySize === size
                                  ? 'bg-accent text-deep-0 border-accent shadow-lg'
                                  : 'bg-deep-surface border-white/10 text-white/40 hover:border-white/30 hover:text-white/60'
                              )}
                            >{size}</button>
                          ))}
                        </div>
                      </div>
                    </div>

                    <div className="flex gap-4 pt-2">
                      <Button variant="outline" size="xl" className="flex-1 text-white" onClick={prevStep}>Back</Button>
                      <Button className="flex-[2]" size="xl" onClick={nextStep}>Next Step <ArrowRight className="ml-3 w-5 h-5" /></Button>
                    </div>
                  </motion.div>
                )}

                {/* ── STEP 3 ── */}
                {step === 3 && (
                  <motion.div key="s3" initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }} className="space-y-8">
                    <div className="space-y-2">
                      <div className="w-11 h-11 rounded-xl bg-accent/10 border border-accent/20 flex items-center justify-center">
                        <Cpu className="w-5 h-5 text-accent" />
                      </div>
                      <p className="text-white/50 text-base font-medium">Regional and security configuration.</p>
                    </div>

                    <div className="space-y-8">
                      <div className="space-y-3">
                        <label className="text-xs font-semibold text-white/50 pl-1">Target Use Cases</label>
                        <div className="flex flex-wrap gap-2">
                          {['KYC', 'Invoices', 'Contracts', 'HR Docs', 'Lending', 'Fraud Detection'].map(u => (
                            <button
                              key={u}
                              onClick={() => {
                                const useCases = orgForm.useCases.includes(u)
                                  ? orgForm.useCases.filter(x => x !== u)
                                  : [...orgForm.useCases, u];
                                setOrgForm({ ...orgForm, useCases });
                              }}
                              className={cn(
                                'px-5 py-2.5 rounded-full text-sm font-medium border transition-all',
                                orgForm.useCases.includes(u)
                                  ? 'bg-accent/10 text-accent border-accent'
                                  : 'bg-deep-surface border-white/10 text-white/40 hover:border-white/30 hover:text-white/60'
                              )}
                            >{u}</button>
                          ))}
                        </div>
                      </div>

                      <div className="grid md:grid-cols-2 gap-6">
                        <div className="space-y-2">
                          <label className="text-xs font-semibold text-white/50 pl-1">Doc Throughput</label>
                          <select
                            className="w-full h-11 px-4 bg-deep-surface border border-white/10 rounded-xl text-sm font-bold text-white focus:ring-2 focus:ring-accent outline-none"
                            value={orgForm.volumeBucket}
                            onChange={e => setOrgForm({ ...orgForm, volumeBucket: e.target.value })}
                          >
                            <option>1,000 - 10,000</option>
                            <option>10,000 - 50,000</option>
                            <option>50,000 - 250,000</option>
                            <option>250,000+</option>
                          </select>
                        </div>

                        <div className="space-y-2">
                          <label className="text-xs font-semibold text-white/50 pl-1">Sovereign Mode</label>
                          <div className="flex gap-1.5 p-1 bg-deep-surface border border-white/10 rounded-xl">
                            {['saas', 'onprem'].map(p => (
                              <button
                                key={p}
                                onClick={() => setOrgForm({ ...orgForm, deploymentPreference: p as 'saas' | 'onprem' })}
                                className={cn(
                                  'flex-1 h-9 rounded-lg text-xs font-semibold transition-all',
                                  orgForm.deploymentPreference === p
                                    ? 'bg-accent text-deep-0 shadow-sm'
                                    : 'text-white/30 hover:text-white/50'
                                )}
                              >
                                {p === 'saas' ? 'Cloud Hub' : 'On-Prem'}
                              </button>
                            ))}
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className="flex gap-4 pt-2">
                      <Button variant="outline" size="xl" className="flex-1 text-white" onClick={prevStep}>Back</Button>
                      <Button className="flex-[2]" size="xl" onClick={nextStep}>Next Step <ArrowRight className="ml-3 w-5 h-5" /></Button>
                    </div>
                  </motion.div>
                )}

                {/* ── STEP 4 ── */}
                {step === 4 && (
                  <motion.div key="s4" initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }} className="space-y-8">
                    <div className="space-y-2">
                      <div className="w-11 h-11 rounded-xl bg-accent/10 border border-accent/20 flex items-center justify-center">
                        <CheckCircle2 className="w-5 h-5 text-accent" />
                      </div>
                      <p className="text-white/50 text-base font-medium">Confirm your deployment settings.</p>
                    </div>

                    <Card className="p-8 border-accent/20 bg-accent/[0.03] space-y-8 relative overflow-hidden">
                      <div className="radar-sweep opacity-5" />
                      <div className="grid grid-cols-2 gap-8 relative z-10">
                        <div className="space-y-1">
                          <p className="text-xs font-semibold text-white/50">Account</p>
                          <p className="text-lg font-bold text-white">{orgForm.name}</p>
                          <p className="text-sm text-white/60">{orgForm.email}</p>
                        </div>
                        <div className="space-y-1">
                          <p className="text-xs font-semibold text-white/50">Enterprise</p>
                          <p className="text-lg font-bold text-white">{orgForm.companyName}</p>
                          <p className="text-sm text-white/60">{orgForm.country}</p>
                        </div>
                        <div className="space-y-1">
                          <p className="text-xs font-semibold text-white/50">Architecture</p>
                          <Badge variant="accent">{orgForm.deploymentPreference.toUpperCase()}_MODE</Badge>
                        </div>
                        <div className="space-y-1">
                          <p className="text-xs font-semibold text-white/50">Target Throughput</p>
                          <p className="text-base font-bold text-white">{orgForm.volumeBucket} /mo</p>
                        </div>
                      </div>
                      <div className="pt-6 border-t border-white/10 space-y-2 relative z-10">
                        <p className="text-xs font-semibold text-white/50">Selected Use Cases</p>
                        <div className="flex flex-wrap gap-2">
                          {orgForm.useCases.map(u => (
                            <span key={u} className="px-3 py-1 bg-white/5 border border-white/10 text-white/70 rounded-full text-xs font-medium">{u}</span>
                          ))}
                        </div>
                      </div>
                    </Card>

                    <div className="space-y-6">
                      <label className="flex items-start gap-4 cursor-pointer p-5 rounded-2xl bg-white/[0.02] border border-white/5 hover:border-white/10 transition-all">
                        <input type="checkbox" checked={agreedToTerms} onChange={e => setAgreedToTerms(e.target.checked)} className="mt-0.5 w-5 h-5 rounded-lg bg-deep-surface border-white/10 accent-accent shrink-0" />
                        <span className="text-sm text-white/60 leading-relaxed font-medium">
                          I authorize the creation of a workspace for{' '}
                          <span className="text-white font-bold">{orgForm.companyName}</span>. I agree to the{' '}
                          <a href="/terms" target="_blank" rel="noopener noreferrer" className="text-accent font-semibold hover:underline cursor-pointer">Terms of Service</a>.
                        </span>
                      </label>

                      <div className="flex gap-4">
                        <Button variant="outline" size="xl" className="flex-1 text-white" onClick={prevStep}>Back</Button>
                        <Button className="flex-[2]" size="xl" isLoading={isLoading} disabled={!agreedToTerms} onClick={handleOrgSubmit}>
                          Complete Setup <Zap className="ml-3 w-5 h-5" />
                        </Button>
                      </div>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          </div>
        )}

        <footer className="mt-16 py-8 border-t border-white/5 text-center relative z-10">
          <p className="text-xs font-semibold text-white/30 uppercase tracking-widest">Secure Encrypted Setup</p>
        </footer>
      </div>
    </div>
  );
};

export default Signup;